import React, { useState } from 'react';
import { Network, Brain, Lightbulb, TrendingUp, Calculator, BookOpen, Zap, GraduationCap } from 'lucide-react';
import MathsSolver from './Maths Knowledge Graph/MathsSolver';
import KnowledgeGraphVisualization from './KnowledgeGraphVisualization';
import KnowledgeGraphSamples from './KnowledgeGraphSamples';
import HKDSEMathsVisual from './HKDSEMathsVisual';

const KnowledgeGraph = () => {
  const [activeView, setActiveView] = useState('main'); // 'main', 'solver', 'samples', 'hkdse', 'visualization'
  const [selectedProblemType, setSelectedProblemType] = useState('quadratic');
  const [selectedSample, setSelectedSample] = useState(null);

  // Simple LaTeX renderer for the selected sample problem
  const renderLatexText = (text) => {
    if (!text) return '';
    
    // Simple LaTeX to HTML conversion for basic expressions
    let processedText = text;
    
    // Convert LaTeX fractions
    processedText = processedText.replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '<span class="frac"><span class="num">$1</span><span class="denom">$2</span></span>');
    
    // Convert LaTeX superscripts
    processedText = processedText.replace(/\^(\d+)/g, '<sup>$1</sup>');
    processedText = processedText.replace(/\^\{([^}]+)\}/g, '<sup>$1</sup>');
    
    // Convert LaTeX subscripts
    processedText = processedText.replace(/_(\d+)/g, '<sub>$1</sub>');
    processedText = processedText.replace(/_\{([^}]+)\}/g, '<sub>$1</sub>');
    
    // Convert LaTeX symbols
    processedText = processedText.replace(/\\alpha/g, 'α');
    processedText = processedText.replace(/\\beta/g, 'β');
    processedText = processedText.replace(/\\gamma/g, 'γ');
    processedText = processedText.replace(/\\delta/g, 'δ');
    processedText = processedText.replace(/\\theta/g, 'θ');
    processedText = processedText.replace(/\\pi/g, 'π');
    processedText = processedText.replace(/\\infty/g, '∞');
    processedText = processedText.replace(/\\leq/g, '≤');
    processedText = processedText.replace(/\\geq/g, '≥');
    processedText = processedText.replace(/\\neq/g, '≠');
    processedText = processedText.replace(/\\approx/g, '≈');
    processedText = processedText.replace(/\\pm/g, '±');
    processedText = processedText.replace(/\\times/g, '×');
    processedText = processedText.replace(/\\div/g, '÷');
    processedText = processedText.replace(/\\sqrt/g, '√');
    processedText = processedText.replace(/\\sum/g, '∑');
    processedText = processedText.replace(/\\int/g, '∫');
    
    // Convert dollar signs to math mode
    processedText = processedText.replace(/\$([^$]+)\$/g, '<span class="math-mode">$1</span>');
    
    return processedText;
  };

  // CSS styles for LaTeX rendering
  const latexStyles = `
    <style>
      .frac {
        display: inline-block;
        vertical-align: middle;
        text-align: center;
        margin: 0 2px;
      }
      .frac .num {
        display: block;
        border-bottom: 1px solid;
        padding: 0 2px;
        font-size: 0.9em;
      }
      .frac .denom {
        display: block;
        padding: 0 2px;
        font-size: 0.9em;
      }
      .math-mode {
        font-family: 'Computer Modern', serif;
        font-style: italic;
      }
      sup, sub {
        font-size: 0.75em;
        line-height: 1;
      }
    </style>
  `;

  const problemTypes = [
    { id: 'quadratic', name: 'Quadratic Equations', icon: Calculator, description: 'Solve quadratic equations with interactive knowledge graphs' },
    { id: 'circle_geometry', name: 'Circle Geometry', icon: Network, description: 'Explore circle equations and geometric relationships' },
    { id: 'factor_remainder', name: 'Factor & Remainder', icon: BookOpen, description: 'Understand polynomial division and remainder theorem' },
    { id: 'probability', name: 'Probability', icon: TrendingUp, description: 'Analyze probability distributions and combinations' },
    { id: 'statistics', name: 'Statistics', icon: Brain, description: 'Calculate central tendency and variability measures' },
    { id: 'trigonometry', name: 'Trigonometry', icon: Lightbulb, description: 'Work with trigonometric functions and special angles' }
  ];

  const renderMainView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-800 mb-4">Knowledge Graph</h2>
        <p className="text-gray-600 text-lg">
          數學知識圖譜 - 探索概念之間的關聯和學習路徑
        </p>
      </div>

      {/* Quick Actions - Updated to 4 buttons */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <button
          onClick={() => setActiveView('solver')}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-xl shadow-lg hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
        >
          <Calculator className="w-8 h-8 mb-3" />
          <h3 className="text-xl font-semibold mb-2">Interactive Solver</h3>
          <p className="text-indigo-100 text-sm">Enter your own mathematical problems and generate knowledge graphs</p>
        </button>

        <button
          onClick={() => setActiveView('samples')}
          className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 rounded-xl shadow-lg hover:from-emerald-700 hover:to-teal-700 transition-all duration-200 transform hover:scale-105"
        >
          <BookOpen className="w-8 h-8 mb-3" />
          <h3 className="text-xl font-semibold mb-2">Sample Problems</h3>
          <p className="text-emerald-100 text-sm">Explore pre-built knowledge graphs for common mathematical topics</p>
        </button>

        <button
          onClick={() => setActiveView('hkdse')}
          className="bg-gradient-to-r from-violet-600 to-purple-600 text-white p-6 rounded-xl shadow-lg hover:from-violet-700 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
        >
          <GraduationCap className="w-8 h-8 mb-3" />
          <h3 className="text-xl font-semibold mb-2">HKDSE Maths</h3>
          <p className="text-violet-100 text-sm">HKDSE Mathematics Paper 1 questions with knowledge graphs</p>
        </button>

        <button
          onClick={() => setActiveView('visualization')}
          className="bg-gradient-to-r from-amber-600 to-orange-600 text-white p-6 rounded-xl shadow-lg hover:from-amber-700 hover:to-orange-700 transition-all duration-200 transform hover:scale-105"
        >
          <Network className="w-8 h-8 mb-3" />
          <h3 className="text-xl font-semibold mb-2">Visualization</h3>
          <p className="text-amber-100 text-sm">Interactive 3D knowledge graph visualization and exploration</p>
        </button>
      </div>

      {/* Problem Type Selection */}
      <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-200">
        <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Zap className="w-5 h-5 text-indigo-600" />
          Choose Problem Type
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {problemTypes.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => {
                  setSelectedProblemType(type.id);
                  setActiveView('solver');
                }}
                className={`p-4 rounded-lg border-2 transition-all duration-200 hover:shadow-md ${
                  selectedProblemType === type.id
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-indigo-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon className={`w-6 h-6 ${
                    selectedProblemType === type.id ? 'text-indigo-600' : 'text-gray-600'
                  }`} />
                  <div className="text-left">
                    <h4 className="font-semibold text-gray-800">{type.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{type.description}</p>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Feature Preview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
            <Network className="w-6 h-6 text-blue-600" />
          </div>
          <h4 className="font-semibold text-gray-800 mb-2">概念關聯圖</h4>
          <p className="text-gray-600 text-sm">
            視覺化展示數學概念之間的關係，幫助理解知識結構
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
            <Brain className="w-6 h-6 text-green-600" />
          </div>
          <h4 className="font-semibold text-gray-800 mb-2">智能學習路徑</h4>
          <p className="text-gray-600 text-sm">
            AI驅動的個人化學習建議，基於您的學習進度
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
            <TrendingUp className="w-6 h-6 text-purple-600" />
          </div>
          <h4 className="font-semibold text-gray-800 mb-2">進度分析</h4>
          <p className="text-gray-600 text-sm">
            詳細的學習分析報告，識別需要加強的領域
          </p>
        </div>
      </div>
    </div>
  );

  const renderSolverView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => {
            setActiveView('main');
            setSelectedSample(null);
          }}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Knowledge Graph
        </button>
        <h2 className="text-2xl font-bold text-gray-800">Interactive Mathematical Solver</h2>
      </div>
      
      {/* Show selected sample info if available */}
      {selectedSample && selectedSample.graphData && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-blue-900">Selected Sample Problem</h3>
              <div 
                className="text-blue-700 text-sm mt-1"
                dangerouslySetInnerHTML={{ __html: renderLatexText(selectedSample.problem) }}
              />
              <div className="flex items-center gap-2 mt-2">
                <span className="inline-block px-2 py-1 text-xs rounded-full bg-blue-200 text-blue-800 font-medium">
                  {selectedSample.difficulty}
                </span>
                <span className="text-xs text-blue-600">
                  {selectedSample.graphData.nodes.length} entities, {selectedSample.graphData.links.length} relationships
                </span>
              </div>
            </div>
            <button
              onClick={() => setSelectedSample(null)}
              className="text-blue-600 hover:text-blue-800 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
      
      <MathsSolver 
        problemType={selectedProblemType} 
        selectedSample={selectedSample}
        onSampleClear={() => setSelectedSample(null)}
        onSampleUpdate={(newSample) => setSelectedSample(newSample)}
      />
    </div>
  );

  const renderSamplesView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveView('main')}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Knowledge Graph
        </button>
        <h2 className="text-2xl font-bold text-gray-800">Sample Knowledge Graphs</h2>
      </div>
      <KnowledgeGraphSamples 
        onSampleSelect={(sample) => {
          setSelectedSample(sample);
          setSelectedProblemType(sample.problemType);
          setActiveView('solver');
        }}
      />
    </div>
  );

  const renderHKDSEView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveView('main')}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Knowledge Graph
        </button>
        <h2 className="text-2xl font-bold text-gray-800">HKDSE Mathematics Paper 1</h2>
      </div>
      <HKDSEMathsVisual 
        onSampleSelect={(sample) => {
          setSelectedSample(sample);
          setSelectedProblemType(sample.problemType);
          setActiveView('solver');
        }}
      />
    </div>
  );

  const renderVisualizationView = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setActiveView('main')}
          className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 transition-colors"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Knowledge Graph
        </button>
        <h2 className="text-2xl font-bold text-gray-800">3D Knowledge Graph Visualization</h2>
      </div>
      <KnowledgeGraphVisualization selectedSample={selectedSample} />
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Add LaTeX styles */}
      <div dangerouslySetInnerHTML={{ __html: latexStyles }} />
      
      <div className="max-w-7xl mx-auto p-6">
        {activeView === 'main' && renderMainView()}
        {activeView === 'solver' && renderSolverView()}
        {activeView === 'samples' && renderSamplesView()}
        {activeView === 'hkdse' && renderHKDSEView()}
        {activeView === 'visualization' && renderVisualizationView()}
      </div>
    </div>
  );
};

export default KnowledgeGraph; 